import React, { useMemo, useState } from 'react';
import { Segmented } from '../../kit';
import {
  HEAT_RAMP,
  axis,
  calendarHeat,
  dowLong,
  dowShort,
  granularities,
  heatLevel,
  heatmap,
  hourLabel,
  hourRange,
  trend,
  calendarWeekdayIndexes,
} from './usageModel';

const GRAN_LABELS = { hour: 'Hour', day: 'Day', week: 'Week', month: 'Month' };
const PLOT_H = 220;

const fmt = (n) => Math.round(n).toLocaleString('en-US');
const oneDecimal = (n) => (n >= 10 ? fmt(n) : (Math.round(n * 10) / 10).toLocaleString('en-US'));

/** Stacked bars of unique active people per day / week / month. */
export function TrendCard({ report, filters, summary, onDrill }) {
  const days = report?.period?.days || 0;
  const { options, fallback } = granularities(days);
  const [granPick, setGran] = useState(null);
  const [split, setSplit] = useState('device');
  const [hover, setHover] = useState(null);
  const gran = options.includes(granPick) ? granPick : fallback;

  const data = useMemo(() => trend(report, filters, gran, split), [report, filters, gran, split]);
  const max = Math.max(1, ...data.buckets.map((b) => b.total));
  const { top, ticks } = axis(max);
  const n = data.buckets.length;
  const gap = n > 60 ? 1 : n > 32 ? 2 : n > 16 ? 4 : 10;
  const labelEvery = Math.max(1, Math.ceil(n / 14));
  const focus = hover !== null && hover < n ? hover : n - 1;
  const current = data.buckets[focus];
  const noun = gran;

  return (
    <section className="ak-card uu-card" aria-labelledby="uu-trend-h">
      <div className="uu-card-head">
        <div className="uu-card-title">
          <h3 id="uu-trend-h">Active people</h3>
          <span className="ak-help">Unique people with at least one {filters.usage === 'email' ? 'report email open' : filters.usage === 'app' ? 'app session' : 'app session or report email open'}, per {noun} · click a bar to see who</span>
        </div>
        <div className="uu-card-actions">
          <span className="ak-help">Split by</span>
          <Segmented
            label="Split by"
            mode="choice"
            options={[{ value: 'device', label: 'Device' }, { value: 'role', label: 'Role' }]}
            value={split}
            onChange={setSplit}
          />
          {options.length > 1 ? (
            <Segmented
              label="Interval"
              options={options.map((o) => ({ value: o, label: GRAN_LABELS[o] }))}
              value={gran}
              onChange={(g) => { setGran(g); setHover(null); }}
            />
          ) : null}
        </div>
      </div>

      <div className="uu-readout" aria-live="polite">
        <span className="uu-readout-label">{current ? current.label : '—'}</span>
        <span className="uu-readout-value">{current ? fmt(current.total) : 0} <span>active</span></span>
        {data.series.map((s, k) => (
          <span key={s.key} className="uu-legend-item">
            <span className="uu-swatch" style={{ background: s.color }} />
            {s.label} <b>{current ? fmt(current.values[k]) : 0}</b>
          </span>
        ))}
      </div>

      <div className="uu-chart" onMouseLeave={() => setHover(null)}>
        <div className="uu-yaxis" style={{ height: PLOT_H }} aria-hidden="true">
          {ticks.map((t) => <span key={t} style={{ top: (1 - t / top) * PLOT_H }}>{fmt(t)}</span>)}
        </div>
        <div className="uu-plot-wrap">
          <div
            className="uu-plot"
            style={{ height: PLOT_H }}
            role="img"
            aria-label={`Active people per ${noun}: ${data.buckets.map((b) => `${b.label} ${b.total}`).join(', ')}`}
          >
            {ticks.map((t) => <div key={t} className={`uu-gridline${t === 0 ? ' uu-base' : ''}`} style={{ top: (1 - t / top) * PLOT_H }} />)}
            <div className="uu-bars" style={{ gap }}>
              {data.buckets.map((b, i) => (
                <button
                  type="button"
                  key={b.key}
                  className={`uu-bar${hover !== null && hover !== i ? ' uu-dim' : ''}`}
                  onMouseEnter={() => setHover(i)}
                  onFocus={() => setHover(i)}
                  aria-label={`${b.label}: ${b.total} active. Show who`}
                  disabled={!b.total}
                  onClick={() => onDrill && onDrill(gran === 'hour'
                    ? { kind: 'hour', dow: b.dow, hour: b.hour, title: `Today · ${b.label}` }
                    : {
                      kind: 'days',
                      days: Array.from({ length: b.lastDay - b.firstDay + 1 }, (_, k) => b.firstDay + k),
                      title: b.label,
                      dayNoun: gran === 'day' ? 'Active' : 'Active days',
                    })}
                >
                  <span className="uu-stack" style={{ height: (b.total / top) * PLOT_H }}>
                    {b.values.map((v, k) => (v > 0 ? (
                      <span key={data.series[k].key} style={{ height: `${(v / b.total) * 100}%`, background: data.series[k].color }} />
                    ) : null))}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div className="uu-xaxis" style={{ gap }} aria-hidden="true">
            {data.buckets.map((b, i) => (
              <div key={b.key}><span>{gran === 'day' || gran === 'hour' ? b.short : (i % labelEvery === 0 ? b.short : '')}</span></div>
            ))}
          </div>
        </div>
      </div>

      <div className="uu-summary">
        <div><span className="ak-help">Avg daily active</span><strong>{oneDecimal(summary.avgDaily)}</strong></div>
        <div><span className="ak-help">Active in period</span><strong>{fmt(summary.active)}</strong></div>
        <div><span className="ak-help">Avg daily in the app</span><strong>{oneDecimal(summary.avgDailyApp)}</strong></div>
        <div><span className="ak-help">Stickiness (daily ÷ period)</span><strong>{Math.round(summary.stickiness * 100)}%</strong></div>
      </div>
    </section>
  );
}

const USAGE_NOUN = { all: 'all usage', app: 'app only', email: 'report emails only' };
const HEAT_MODES = [{ value: 'day', label: 'Day' }, { value: 'hour', label: 'Time of day' }];

function Ramp() {
  return (
    <span className="uu-ramp" aria-hidden="true">
      Fewer
      {HEAT_RAMP.map((c) => <span key={c} style={{ background: c }} />)}
      More
    </span>
  );
}

const CAL_ROW_LABELS = ['Mon', '', 'Wed', '', 'Fri', '', ''];

/**
 * Contribution-style calendar of the last 53 weeks: week columns × Mon–Sun
 * rows, shaded by people active that day. Columns stretch to fill the card.
 */
function dayView(data, { onFocus, focusIndex, onDrill, report }) {
  const cells = data.weeks.flatMap((w) => w.cells).filter(Boolean);
  const focus = focusIndex !== null ? cells.find((c) => c.index === focusIndex) : null;
  const readout = focus
    ? `${focus.label} · ${fmt(focus.value)} active`
    : data.peak ? `Busiest: ${data.peak.label} · ${fmt(data.peak.value)} active` : 'No activity yet';
  return {
    readout,
    body: (
      <div className="uu-daymode">
        <div
          className="uu-contrib"
          style={{ gridTemplateColumns: `auto repeat(${data.weeks.length}, minmax(0, 1fr))` }}
          onMouseLeave={() => onFocus(null)}
          role="group"
          aria-label={data.peak ? `People active each day over the last year. Busiest: ${data.peak.label}, ${data.peak.value} people` : 'No activity in the last year'}
        >
          <span className="uu-contrib-label" aria-hidden="true" />
          {CAL_ROW_LABELS.map((d, i) => <span key={i} className="uu-contrib-label" aria-hidden="true">{d}</span>)}
          {data.weeks.map((w) => (
            <React.Fragment key={w.key}>
              <span className="uu-contrib-month" aria-hidden="true">{w.monthLabel}</span>
              {w.cells.map((c, dow) => (c ? (
                <button
                  type="button"
                  key={dow}
                  className={`uu-contrib-cell${focusIndex === c.index ? ' uu-on' : ''}`}
                  style={{ background: HEAT_RAMP[heatLevel(c.value, data.max)] }}
                  onMouseEnter={() => onFocus(c.index)}
                  onFocus={() => onFocus(c.index)}
                  title={`${c.label} · ${c.value} active`}
                  aria-label={`${c.label}: ${c.value} active. Show who`}
                  disabled={!c.value}
                  onClick={() => onDrill({ kind: 'calendar', days: [c.index], title: c.label, dayNoun: 'Active' })}
                />
              ) : <span key={dow} className="uu-contrib-cell uu-out" />))}
            </React.Fragment>
          ))}
        </div>
        <div className="uu-weekdays">
          <span className="ak-section-label">Avg per day of week</span>
          {data.weekdayAvg.map((v, dow) => (
            <button
              type="button"
              key={dow}
              className="uu-weekday-row uu-drillable"
              disabled={!v}
              aria-label={`${dowLong(dow)}s: ${oneDecimal(v)} active on average. Show who`}
              onClick={() => onDrill({ kind: 'calendar', days: calendarWeekdayIndexes(report, dow), title: `${dowLong(dow)}s · last 12 months`, dayNoun: `${dowLong(dow)}s active` })}
            >
              <span className="uu-weekday-label">{dowShort(dow)}</span>
              <span className="uu-meter"><span style={{ width: `${data.weekdayMax ? (v / data.weekdayMax) * 100 : 0}%` }} /></span>
              <span className="uu-weekday-value">{oneDecimal(v)}</span>
            </button>
          ))}
        </div>
      </div>
    ),
    foot: data.peak
      ? <>{fmt(data.people)} people active in the last 12 months · busiest <strong>{data.peak.label}</strong> ({fmt(data.peak.value)})</>
      : 'No activity in the last 12 months',
  };
}

/** Day-of-week × hour grid of average active people, in the org's local time. */
function hourView(data, { onFocus, focusCell, onDrill }) {
  const focus = focusCell || data.peak;
  const focusValue = focus ? data.grid[focus.dow][focus.hour] : 0;
  return {
    readout: focus ? `${dowLong(focus.dow)} · ${hourRange(focus.hour)} · ${oneDecimal(focusValue)} active` : 'No activity yet',
    body: (
      <div
        className="uu-heat"
        onMouseLeave={() => onFocus(null)}
        role="img"
        aria-label={data.peak ? `Busiest hour: ${dowLong(data.peak.dow)} ${hourRange(data.peak.hour)}, ${oneDecimal(data.peak.value)} people on average` : 'No activity in this period'}
      >
        {data.grid.map((row, dow) => (
          <div key={dow} className="uu-heat-row">
            <span className="uu-heat-day">{dowShort(dow)}</span>
            {row.map((v, hour) => (
              <button
                type="button"
                key={hour}
                className={`uu-heat-cell${focusCell && focusCell.dow === dow && focusCell.hour === hour ? ' uu-on' : ''}`}
                style={{ background: HEAT_RAMP[heatLevel(v, data.max)] }}
                onMouseEnter={() => onFocus({ dow, hour })}
                onFocus={() => onFocus({ dow, hour })}
                aria-label={`${dowLong(dow)} ${hourRange(hour)}: ${oneDecimal(v)} active on average. Show who`}
                disabled={!v}
                onClick={() => onDrill({ kind: 'hour', dow, hour, title: `${dowLong(dow)}s · ${hourRange(hour)}` })}
              />
            ))}
          </div>
        ))}
        <div className="uu-heat-row uu-heat-hours" aria-hidden="true">
          <span className="uu-heat-day" />
          {Array.from({ length: 24 }, (_, h) => <div key={h}>{h % 3 === 0 ? hourLabel(h) : ''}</div>)}
        </div>
      </div>
    ),
    foot: data.peak ? <>Busiest: <strong>{dowLong(data.peak.dow)}, {hourRange(data.peak.hour)}</strong></> : 'No activity in this period',
  };
}

/**
 * When people use Lodgeic: a contribution-style calendar of the period by
 * default, or day of week × hour of day.
 */
export function HeatmapCard({ report, filters, onDrill }) {
  const [mode, setMode] = useState('day');
  const [focusDay, setFocusDay] = useState(null);
  const [focusCell, setFocusCell] = useState(null);
  const dayData = useMemo(() => (mode === 'day' ? calendarHeat(report, filters) : null), [mode, report, filters]);
  const hourData = useMemo(() => (mode === 'hour' ? heatmap(report, filters) : null), [mode, report, filters]);
  const view = mode === 'day'
    ? dayView(dayData, { onFocus: setFocusDay, focusIndex: focusDay, onDrill, report })
    : hourView(hourData, { onFocus: setFocusCell, focusCell, onDrill });
  const subtitle = mode === 'day'
    ? `People active each day, last 12 months · ${USAGE_NOUN[filters.usage] || USAGE_NOUN.all} · click a day to see who`
    : `Average active people per hour · ${USAGE_NOUN[filters.usage] || USAGE_NOUN.all} · ${report?.timezone || 'local time'} · click a cell to see who`;

  return (
    <section className="ak-card uu-card" aria-labelledby="uu-heat-h">
      <div className="uu-card-head">
        <div className="uu-card-title">
          <h3 id="uu-heat-h">When people use Lodgeic</h3>
          <span className="ak-help">{subtitle}</span>
        </div>
        <div className="uu-card-actions">
          <span className="uu-chip-readout" aria-live="polite">{view.readout}</span>
          <Segmented label="Heatmap view" options={HEAT_MODES} value={mode} onChange={(m) => { setMode(m); setFocusDay(null); setFocusCell(null); }} />
        </div>
      </div>
      {view.body}
      <div className="uu-heat-foot">
        <span className="ak-help">{view.foot}</span>
        <Ramp />
      </div>
    </section>
  );
}

function MeterRows({ rows, value, sub, width, alt, onPick }) {
  return (
    <div className="uu-meters">
      {rows.map((r) => (
        <button
          type="button"
          key={r.key}
          className="uu-meter-row uu-drillable"
          onClick={() => onPick(r)}
          aria-label={`${r.label}: ${value(r)}${sub ? `, ${sub(r)}` : ''}. Show who`}
        >
          <span className="uu-meter-text">
            <span className="uu-meter-label">{r.label}</span>
            {sub ? <span className="ak-help">{sub(r)}</span> : null}
            <span className="uu-meter-value">{value(r)}</span>
          </span>
          <span className={`uu-meter${alt ? ' uu-alt' : ''}`}><span style={{ width: `${width(r)}%` }} /></span>
        </button>
      ))}
    </div>
  );
}

export function BreakdownCards({ summary, onDrill }) {
  const maxApp = Math.max(1, ...summary.appTypes.map((r) => r.people));
  const maxOpens = Math.max(1, ...summary.emailTypes.map((r) => r.opens));
  return (
    <div className="uu-grid3">
      <section className="ak-card uu-card" aria-labelledby="uu-role-h">
        <div className="uu-card-title">
          <h3 id="uu-role-h">By role</h3>
          <span className="ak-help">Active in the period, of everyone with that role</span>
        </div>
        {summary.byRole.length ? (
          <MeterRows rows={summary.byRole} sub={(r) => `${r.active} of ${r.total}`} value={(r) => `${r.pct}%`} width={(r) => r.pct}
            onPick={(r) => onDrill({ kind: 'role', key: r.key, title: r.label, sub: `${r.active} of ${r.total} active` })} />
        ) : <p className="ak-help">No one in scope.</p>}
      </section>
      <section className="ak-card uu-card" aria-labelledby="uu-tag-h">
        <div className="uu-card-title">
          <h3 id="uu-tag-h">By tag</h3>
          <span className="ak-help">Someone with several tags counts under each</span>
        </div>
        {summary.byTag.length ? (
          <MeterRows rows={summary.byTag.slice(0, 8)} sub={(r) => `${r.active} of ${r.total}`} value={(r) => `${r.pct}%`} width={(r) => r.pct}
            onPick={(r) => onDrill({ kind: 'tag', key: r.key, title: `Tag: ${r.label}`, sub: `${r.active} of ${r.total} active` })} />
        ) : <p className="ak-help">No one in scope has a tag yet. Tag people on their profile to compare groups here.</p>}
      </section>
      <section className="ak-card uu-card" aria-labelledby="uu-type-h">
        <div className="uu-card-title">
          <h3 id="uu-type-h">By usage type</h3>
          <span className="ak-help">People can appear in more than one row</span>
        </div>
        <span className="ak-section-label">App</span>
        <MeterRows rows={summary.appTypes} value={(r) => `${fmt(r.people)} ${r.people === 1 ? 'person' : 'people'}`} width={(r) => (r.people / maxApp) * 100}
          onPick={(r) => onDrill({ kind: 'channel', channel: r.key, title: r.label })} />
        <span className="ak-section-label">Report emails</span>
        {summary.emailTypes.length ? (
          <MeterRows
            rows={summary.emailTypes}
            sub={(r) => `${r.readers} ${r.readers === 1 ? 'reader' : 'readers'}`}
            value={(r) => `${fmt(r.opens)} opens`}
            width={(r) => (r.opens / maxOpens) * 100}
            alt
            onPick={(r) => onDrill({ kind: 'report', report: r.key, title: `${r.label} email`, dayNoun: 'Days opened' })}
          />
        ) : <p className="ak-help">No report emails opened in this period.</p>}
      </section>
    </div>
  );
}
